import { EmailEngineSevice } from "../../../../../../../server/api/email-engine-v2";
import {
  useApiLoader,
  type UseApiLoaderResponse,
} from "../../../../../../../server/api/hooks/use-api-loader";
import { useToken } from "../../../../../../../server/auth/use-token";
import type { UseSupabaseLoaderState } from "../../../../../../../server/supabase/hooks/types";
import { Waiting } from "../../../../../../../server/supabase/hooks/use-supabase-loader";
import type { SerialisedLexicalEditorDesign } from "../../../../../../message/editor/types/serialised-editor-design";
import type { MomentPreviewTags } from "../../../../../server/supabase-preview-service";
import type { PreviewMomentData } from "../types";
import type { MomentPreviewInfo } from "./use-preview-info";
import { usePreviewTags } from "./use-preview-tags";
import { useSurveyPreviewData } from "./use-survey-preview-data";

type Params = {
  body: string | undefined;
  dynamic_segment_id_map: PreviewMomentData["dynamic_segment_id_map"];
  preview_info: UseSupabaseLoaderState<MomentPreviewInfo | null>;
  preview_test_data: UseSupabaseLoaderState<MomentPreviewTags | null>;
  design: SerialisedLexicalEditorDesign | null;
  subject: string | null;
  email_additional_merge_fields: string[];
  use_unlayer_email_preview: boolean;
  dynamic_condition_fields?: Record<string, boolean>;
};

export function useBuiltHtmlMessage({
  body,
  dynamic_segment_id_map,
  preview_info,
  preview_test_data,
  design,
  subject,
  email_additional_merge_fields,
  use_unlayer_email_preview,
  dynamic_condition_fields,
}: Params): UseApiLoaderResponse<string> {
  const getToken = useToken();

  const { loading: mergeFieldsLoading, data: mergeFields } = usePreviewTags({
    dynamic_segment_id_map,
    preview_info,
    preview_test_data,
    design,
    subject,
    email_additional_merge_fields,
    use_unlayer_email_preview,
  });

  const { loading: surveyLoading, data: surveyData } = useSurveyPreviewData({ body });

  return useApiLoader(async () => {
    if (mergeFieldsLoading || surveyLoading) throw new Waiting();

    const token = await getToken();

    const service = new EmailEngineSevice(token);

    const contentMailMergeFields = Object.fromEntries(
      (mergeFields?.tags ?? []).map((field) => [field.key, field.value ?? ""]),
    );

    const surveyMergeFields = surveyData
      ? ({ [surveyData.key]: surveyData.value } as unknown as Record<string, string | null>)
      : {};

    const response = await service.buildMessage({
      body: body ?? "",
      mail_merge_fields: { person_id: "test", ...contentMailMergeFields, ...surveyMergeFields },
      dynamic_condition_fields:
        dynamic_condition_fields ?? mergeFields?.dynamic_condition_fields ?? {},
      mode: "html",
    });

    if (response.error || !response.data)
      throw new Error(response.error ?? "Error building message");

    const data = response.data.data;

    // Create a DOM parser to modify the HTML content before setting it in the iframe
    const parser = new DOMParser();
    const doc = parser.parseFromString(data, "text/html");

    // Find all links and update them to open in new tabs
    const links = doc.querySelectorAll("a");
    links.forEach((link) => {
      link.setAttribute("target", "_blank");
      link.setAttribute("rel", "noopener noreferrer");
    });

    // Convert back to string
    return doc.documentElement.outerHTML;
  }, [
    mergeFieldsLoading,
    surveyLoading,
    getToken,
    mergeFields?.tags,
    mergeFields?.dynamic_condition_fields,
    body,
    dynamic_condition_fields,
    surveyData,
  ]);
}
